// pages/lab/briefs/index.js
// ════════════════════════════════════════════════════════════════════
// Lab Weekly Briefs — index page (Phase F14).
// Lists every brief most-recent-first. Cards are reading-list-shaped:
// week-of, title, lede, cited count.
// ════════════════════════════════════════════════════════════════════

import Head from 'next/head';
import Link from 'next/link';
import { listBriefs } from '../../../data/lab/briefs';

export async function getStaticProps() {
  return { props: { briefs: listBriefs() } };
}

export default function BriefsIndexPage({ briefs }) {
  return (
    <div className="sp-root">
      <Head>
        <title>Weekly Briefs · Lab · Shift</title>
        <meta
          name="description"
          content="Weekly cross-corpus syntheses. What moved across tracked shifts + findings + predictions this week."
        />
      </Head>

      <div className="sp-page">
        <div className="findings-index-topbar">
          <Link href="/findings" className="finding-back">← Findings index</Link>
          <div className="findings-index-topbar-meta">
            Lab · Weekly Briefs · <Link href="/lab/briefs/new" className="methodology-inline-link">+ New brief</Link>
          </div>
        </div>

        <header className="findings-index-header">
          <div className="finding-eyebrow">§ Weekly cadence</div>
          <h1 className="findings-index-title">Weekly Briefs.</h1>
          <p className="findings-index-sub">
            Briefs are cross-corpus weekly syntheses — not findings. They
            track what moved across the corpus this week, list predictions
            approaching resolution, and flag what to watch next. Each brief
            cites the underlying findings + shifts so the reader can drill
            into the rigorous claim. Read the
            {' '}<Link href="/lab/methodology" className="methodology-inline-link">methodology</Link> for
            the artifact distinction.
          </p>
        </header>

        <section className="findings-index-stats">
          <Stat label="Briefs published" value={briefs.length} />
          <Stat label="Most recent" value={briefs[0]?.weekOf || '—'} />
          <Stat label="Cadence" value="Weekly" />
        </section>

        <section className="briefs-index-grid">
          {briefs.map((b) => (
            <BriefCard key={b.id} brief={b} />
          ))}
        </section>

        <footer className="findings-index-footer">
          <p>
            A brief is the lighter weekly artifact. The load-bearing claims
            live in the <Link href="/findings" className="methodology-inline-link">findings corpus</Link>;
            briefs are how the corpus gets read on a weekly cadence.
          </p>
        </footer>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="findings-index-stat">
      <div className="findings-index-stat-value">{value}</div>
      <div className="findings-index-stat-label">{label}</div>
    </div>
  );
}

function BriefCard({ brief }) {
  const findingsCount = brief.citedFindings?.length || 0;
  const shiftsCount = brief.citedShifts?.length || 0;
  return (
    <Link href={`/lab/briefs/${brief.slug}`} className="briefs-index-card">
      <div className="briefs-index-card-meta">
        <span className="briefs-index-card-week">{brief.weekOf}</span>
        <span className="briefs-index-card-date">{brief.publishDate}</span>
      </div>
      <h2 className="briefs-index-card-title">{brief.title}</h2>
      {brief.description && (
        <p className="briefs-index-card-desc">{brief.description}</p>
      )}
      <div className="briefs-index-card-foot">
        {findingsCount > 0 && (
          <span className="briefs-index-card-foot-item">
            cites {findingsCount} finding{findingsCount === 1 ? '' : 's'}
          </span>
        )}
        {shiftsCount > 0 && (
          <span className="briefs-index-card-foot-item">
            {shiftsCount} shift{shiftsCount === 1 ? '' : 's'}
          </span>
        )}
        {brief.predictionsOnDeck?.length > 0 && (
          <span className="briefs-index-card-foot-item">
            {brief.predictionsOnDeck.length} predictions tracked
          </span>
        )}
        <span className="briefs-index-card-foot-arrow">Read →</span>
      </div>
    </Link>
  );
}
